import { useCallback } from 'react';
import { useRecoilState } from 'recoil';
import type { Image3D } from '~/domain/patient/image3d';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { seriesMipPreviewVisibleState } from '~/state/atoms/viewerAtoms';

/** The series overview: whether it is shown, what it projects, and its switch. */
export interface SeriesOverview {
    /** Whether the user asked for the overview. */
    readonly isOpen: boolean;
    /**
     * The image the overview projects: the primary image, whatever tile
     * is being edited. Undefined while none is selected, when there is
     * nothing to show.
     */
    readonly image: Image3D | undefined;
    readonly toggle: () => void;
}

/**
 * The series overview of the primary image, as the control that shows it
 * and the panel that draws it both read it.
 */
export function useSeriesOverview(): SeriesOverview {
    const [isOpen, setOpen] = useRecoilState(seriesMipPreviewVisibleState);
    const image = useSelectedPrimaryImage();
    const toggle = useCallback(() => setOpen((open) => !open), [setOpen]);
    return { isOpen, image, toggle };
}
